// Social Media Hub Academy — course content, Part B (Sections 8–14)
import type { TrainingSection } from './trainingTypes';

export const SECTIONS_B: TrainingSection[] = [
  // 8 ───────────────────────────────────────────────────────────────────────
  {
    id: 'approval',
    num: 8,
    title: 'Approval Workflow',
    short: 'Approval',
    icon: 'shield',
    minutes: 8,
    intro:
      'Nothing reaches your audience by accident. The approval workflow moves every post through clearly defined states — from draft to published — with a paper trail at each step.',
    objectives: [
      'Name every approval state and what triggers it',
      'Approve, reject, and request revisions correctly',
      'Understand who is responsible at each stage',
    ],
    slides: [
      {
        id: 'appr-states',
        title: 'The six states of a post',
        kicker: 'From idea to live',
        blocks: [
          {
            type: 'workflow',
            title: 'Approval pipeline',
            stages: [
              { label: 'Draft', desc: 'Freshly generated or manually created — fully editable', tone: 'neutral' },
              { label: 'Internal Review', desc: 'The content team checks copy, media and schedule', tone: 'blue' },
              { label: 'Admin Approval', desc: 'An administrator gives the final sign-off', tone: 'gold' },
              { label: 'Published', desc: 'Exported / posted to the connected platform', tone: 'green' },
              { label: 'Rejected', desc: 'Declined with a reason — will not publish', tone: 'red' },
              { label: 'Revision Requested', desc: 'Sent back with specific notes for rework', tone: 'red' },
            ],
          },
          {
            type: 'callout',
            tone: 'info',
            title: 'The paper trail',
            text: 'Every state change is recorded: who acted, when, and any note they left. Disputes about “who approved this?” simply cannot happen.',
          },
        ],
        notes:
          'Walk the pipeline left to right, then highlight the two red exits. Stress that Rejected is terminal (start over) while Revision Requested is a loop back to Draft with notes. For enterprise clients, the audit trail is often the single feature that closes the deal.',
      },
      {
        id: 'appr-practice',
        title: 'Approving like a professional',
        blocks: [
          {
            type: 'steps',
            title: 'The reviewer checklist',
            items: [
              { title: 'Voice check', desc: 'Does the caption sound like the brand? Compare against the voice profile.' },
              { title: 'Template check', desc: 'Does the image match the template’s locked visual style?' },
              { title: 'Facts check', desc: 'Place names, prices, dates, spelling of local terms.' },
              { title: 'Schedule check', desc: 'Right day, right time, no collisions with other posts.' },
              { title: 'Decide', desc: 'Approve, request a specific revision, or reject with a reason.' },
            ],
          },
          {
            type: 'dodont',
            dos: ['Write actionable revision notes: “shorten hook to 6 words”', 'Review a whole week in one sitting for consistency', 'Approve on mobile when you are travelling — it is fully supported'],
            donts: ['Request revisions without saying what to change', 'Approve posts with placeholder images “to fix later”', 'Let posts sit in review for days — momentum matters'],
          },
          {
            type: 'check',
            question: 'A post needs a better hook but everything else is perfect. What state should you send it to?',
            choices: ['Revision Requested — with a note about the hook', 'Rejected', 'Published', 'Delete it'],
            correctIndex: 0,
            explain: 'Revision Requested loops the post back with notes. Rejection is for posts that should never run; deletion destroys good work.',
          },
        ],
        notes:
          'Roleplay this in demos: put a deliberately flawed post on screen and have the audience run the five-point checklist aloud. It teaches the workflow and proves the UI supports it.',
        takeaways: [
          'Six states: Draft → Internal Review → Admin Approval → Published (or Rejected / Revision Requested)',
          'Revision Requested loops back; Rejected is final',
          'Every action is logged — full accountability',
        ],
      },
    ],
  },

  // 9 ───────────────────────────────────────────────────────────────────────
  {
    id: 'scheduling',
    num: 9,
    title: 'Scheduling System',
    short: 'Scheduling',
    icon: 'calendar',
    minutes: 6,
    intro:
      'The scheduler turns approved content into a calm, predictable publishing rhythm — auto-assigning optimal days and times, with full manual override.',
    objectives: [
      'Auto-schedule a generated week',
      'Manually adjust day, time and format per post',
      'Apply posting-time best practices for travel audiences',
    ],
    slides: [
      {
        id: 'sched-how',
        title: 'Auto-scheduling and manual control',
        blocks: [
          {
            type: 'steps',
            title: 'How a week gets scheduled',
            items: [
              { title: 'Generate', desc: 'Each of the 7 posts is born with a proposed day (Mon–Sun) and time slot.' },
              { title: 'Auto-schedule', desc: 'The Auto-Schedule assistant distributes posts across proven engagement windows.' },
              { title: 'Adjust', desc: 'Override any post’s day or time in the Schedule & Format section of its card.' },
              { title: 'Verify', desc: 'The Content Planner calendar shows the final rhythm at a glance.' },
            ],
          },
          {
            type: 'bullets',
            title: 'Engagement windows that work for luxury travel',
            items: [
              'Weekday mornings 7–9 am — commuters dreaming of escapes',
              'Lunchtime 12–1 pm — quick scroll peak',
              'Evenings 7–9 pm — planning mode, highest saves',
              'Sunday evening — the strongest “book the trip” intent of the week',
            ],
          },
          {
            type: 'check',
            question: 'When is “book the trip” intent typically highest for travel audiences?',
            choices: ['Sunday evening', 'Monday 6 am', 'Friday midnight', 'Wednesday 3 pm'],
            correctIndex: 0,
            explain: 'Sunday evening scrollers are planning their next escape — prime time for itinerary and deal posts.',
          },
        ],
        notes:
          'Emphasize that auto-scheduling is a starting point, not a cage. Power users let the assistant place the week, then hand-tune two or three posts. Mention timezone awareness: schedule in the AUDIENCE’s timezone, not yours.',
        takeaways: [
          'Auto-schedule first, hand-tune second',
          'Every post’s day/time/format is individually editable',
          'Sunday evening is prime travel-intent time',
        ],
      },
    ],
  },

  // 10 ──────────────────────────────────────────────────────────────────────
  {
    id: 'publishing',
    num: 10,
    title: 'Publishing',
    short: 'Publishing',
    icon: 'send',
    minutes: 6,
    intro:
      'Step 5 turns approved content into live content: Instagram connection, export packages, and the Ready-to-Export deliverables panel.',
    objectives: [
      'Connect an Instagram account to the Hub',
      'Export a fully approved week as a deliverables package',
      'Choose the right format: feed, carousel, reel, or story',
    ],
    slides: [
      {
        id: 'pub-flow',
        title: 'From approved to live',
        blocks: [
          {
            type: 'workflow',
            title: 'Publishing paths',
            stages: [
              { label: 'Approve', desc: 'Only Admin-Approved posts reach Step 5', tone: 'gold' },
              { label: 'Connect', desc: 'Link the Instagram account via Instagram Connect', tone: 'blue' },
              { label: 'Export', desc: 'Ready-to-Export bundles captions, hashtags, media and schedule', tone: 'neutral' },
              { label: 'Publish', desc: 'Post directly or hand the package to any scheduler', tone: 'green' },
            ],
          },
          {
            type: 'hotspots',
            title: 'Supported content formats',
            items: [
              { label: 'Feed post', detail: 'Single image + caption. The backbone of the grid; where template styles shine.' },
              { label: 'Carousel', detail: 'Hero + supporting shots as a swipeable set — perfect for itineraries and hotel showcases.' },
              { label: 'Reel', detail: 'Video-first. Pair with the Cinematic Travel Reel template’s shot sequence.' },
              { label: 'Story', detail: 'Ephemeral, casual, interactive. Planned separately in the Stories Planner.' },
            ],
          },
          {
            type: 'callout',
            tone: 'warn',
            title: 'Gate rule',
            text: 'Unapproved posts never export. If a post is missing from your package, check its approval state first — that is the answer 95% of the time.',
          },
          {
            type: 'check',
            question: 'A post is missing from the export package. Most likely cause?',
            choices: ['It has not been admin-approved', 'The image is too large', 'Instagram is down', 'The caption is too short'],
            correctIndex: 0,
            explain: 'Export only includes Admin-Approved posts. Approval state is always the first thing to check.',
          },
        ],
        notes:
          'Demo the Ready-to-Export panel with a fully approved week: show that captions, hashtags, media and times all travel together. The “gate rule” prevents the classic disaster of a draft going live — say it exactly like that.',
        takeaways: [
          'Only approved posts export — no accidents',
          'Four formats: feed, carousel, reel, story',
          'Export packages carry everything: copy, media, schedule',
        ],
      },
    ],
  },

  // 11 ──────────────────────────────────────────────────────────────────────
  {
    id: 'analytics',
    num: 11,
    title: 'Analytics',
    short: 'Analytics',
    icon: 'chart',
    minutes: 6,
    intro:
      'What gets measured gets improved. Analytics closes the loop: engagement per post, template performance, and week-over-week trends feed back into what you generate next.',
    objectives: [
      'Read the core engagement metrics',
      'Compare performance by content template',
      'Turn insights into next week’s template mix',
    ],
    slides: [
      {
        id: 'an-metrics',
        title: 'Metrics that matter',
        blocks: [
          {
            type: 'hotspots',
            title: 'The luxury-travel metric stack',
            items: [
              { label: 'Reach', detail: 'Unique accounts that saw a post. Your top-of-funnel awareness number.' },
              { label: 'Engagement rate', detail: 'Likes + comments + saves + shares relative to reach. Quality of attention.' },
              { label: 'Saves', detail: 'THE travel metric — a save means “I want to go there.” Weight it above likes.' },
              { label: 'Shares', detail: 'Someone sent your destination to a travel partner. Highest-intent signal.' },
              { label: 'Profile visits', detail: 'Post-to-profile clicks show brand curiosity turning into consideration.' },
              { label: 'Template performance', detail: 'Which of the 25 templates earns the most saves? That answer shapes next week’s mix.' },
            ],
          },
          {
            type: 'callout',
            tone: 'gold',
            title: 'The feedback loop',
            text: 'If Hidden Gems posts out-save Hotel Showcases 3-to-1 this month, generate more Hidden Gems next month. Analytics is not a report — it is the steering wheel.',
          },
          {
            type: 'check',
            question: 'Which metric best signals “I want to travel there” intent?',
            choices: ['Saves', 'Likes', 'Impressions', 'Follower count'],
            correctIndex: 0,
            explain: 'A save is a bookmark for a future trip. Likes are polite; saves are intent.',
          },
        ],
        notes:
          'The one-liner to deliver: “Likes are applause, saves are bookings.” Show a mock comparison of two templates and ask the audience which one they would generate more of next week — let them steer.',
        takeaways: [
          'Saves and shares outrank likes for travel intent',
          'Track performance by template, not just by post',
          'Analytics decides next week’s template mix',
        ],
      },
    ],
  },

  // 12 ──────────────────────────────────────────────────────────────────────
  {
    id: 'brand',
    num: 12,
    title: 'Brand Management',
    short: 'Brand',
    icon: 'gem',
    minutes: 6,
    intro:
      'Brand management keeps every asset — colors, typography, logos, hashtags, templates — consistent across everything the Hub produces.',
    objectives: [
      'Locate and use the Brand Guidelines panel',
      'Understand how palette and voice flow into generation',
      'Manage reusable brand assets and hashtag sets',
    ],
    slides: [
      {
        id: 'brand-system',
        title: 'The Zera brand system',
        blocks: [
          {
            type: 'hotspots',
            title: 'What the brand system controls',
            items: [
              { label: 'Color palette', detail: 'Zera gold (#C9A961), deep navy, warm neutrals — woven into AI image prompts automatically.' },
              { label: 'Typography & tone', detail: 'Elegant, minimal, confident. The voice profile enforces it in copy; guidelines document it for humans.' },
              { label: 'Logos & marks', detail: 'Stored in the brand-assets bucket for use in overlays, stories and exports.' },
              { label: 'Hashtag sets', detail: 'Curated, reusable hashtag groups per content pillar — no more improvising 15 tags at midnight.' },
              { label: 'Content templates', detail: 'The 25-template library IS brand management — each template locks a visual language.' },
            ],
          },
          {
            type: 'dodont',
            dos: ['Keep the guidelines panel open during reviews', 'Update brand assets in one place — they propagate everywhere', 'Retire off-palette media on sight'],
            donts: ['Introduce new colors “just for one post”', 'Let each admin keep private hashtag lists'],
          },
          {
            type: 'check',
            question: 'How does the brand palette reach AI-generated images?',
            choices: ['It is woven into every assembled image prompt automatically', 'Designers recolor each image by hand', 'It does not affect images', 'Only via uploaded photos'],
            correctIndex: 0,
            explain: 'Prompt assembly includes the brand palette alongside the shot and template style — brand consistency is automated.',
          },
        ],
        notes:
          'Key message: brand consistency here is not a PDF nobody reads — it is executed by the software. The palette is IN the prompts; the voice is IN the captions. Governance by architecture, not by memo.',
        takeaways: [
          'Brand palette and voice are enforced automatically in generation',
          'One source of truth for logos, colors, hashtags and templates',
          'Consistency is architectural, not aspirational',
        ],
      },
    ],
  },

  // 13 ──────────────────────────────────────────────────────────────────────
  {
    id: 'campaigns',
    num: 13,
    title: 'Campaign Management',
    short: 'Campaigns',
    icon: 'target',
    minutes: 5,
    intro:
      'Campaigns group weeks and posts around a goal — a seasonal push, a new route launch, a deals blitz — so effort and results stay organized.',
    objectives: [
      'Structure a campaign from weeks and templates',
      'Balance campaign posts with evergreen content',
      'Track a campaign from brief to results',
    ],
    slides: [
      {
        id: 'camp-structure',
        title: 'Anatomy of a campaign',
        blocks: [
          {
            type: 'steps',
            title: 'Building a “Summer Riviera” campaign',
            items: [
              { title: 'Define the goal', desc: 'e.g. 40 villa-week inquiries for July–August.' },
              { title: 'Pick the template mix', desc: 'Destination Spotlights + Luxury Experiences + Travel Deals + Client Highlights.' },
              { title: 'Generate the weeks', desc: 'Create 4 weeks; each gets a date-range name so the campaign timeline is self-evident.' },
              { title: 'Schedule the arc', desc: 'Awareness posts early, deal posts late — a narrative, not a barrage.' },
              { title: 'Measure & adapt', desc: 'Watch saves and profile visits weekly; rebalance templates mid-campaign.' },
            ],
          },
          {
            type: 'callout',
            tone: 'tip',
            title: 'The 70/30 rule',
            text: 'Even during a big campaign, keep ~30% of posts evergreen (culture, food, hidden gems). Feeds that turn into pure promotion bleed followers.',
          },
          {
            type: 'check',
            question: 'During a heavy promotional campaign, what share of content should stay evergreen?',
            choices: ['About 30%', '0% — go all-in', '90%', 'Exactly 50%'],
            correctIndex: 0,
            explain: 'The 70/30 rule keeps the feed feeling like a publication rather than a billboard, protecting long-term engagement.',
          },
        ],
        notes:
          'Use the Summer Riviera walkthrough as a story. The date-range week names make campaign timelines readable at a glance — point that out explicitly.',
        takeaways: [
          'Campaign = goal + template mix + scheduled arc',
          'Date-range week names make timelines self-documenting',
          '70/30: campaign posts to evergreen content',
        ],
      },
    ],
  },

  // 14 ──────────────────────────────────────────────────────────────────────
  {
    id: 'media',
    num: 14,
    title: 'Media Library',
    short: 'Media',
    icon: 'film',
    minutes: 5,
    intro:
      'Photos, videos and graphics live in shared storage buckets, organized for instant reuse across every post type.',
    objectives: [
      'Upload and manage photos, videos and graphics',
      'Organize the universal B-roll library by category',
      'Choose between AI renders and real photography',
    ],
    slides: [
      {
        id: 'media-org',
        title: 'Running a professional media library',
        blocks: [
          {
            type: 'bullets',
            title: 'Core B-roll categories to maintain',
            items: [
              'Aerial drone footage · beaches · mountains · rivers & waterfalls',
              'Luxury hotels · infinity pools · spas · rooftop bars · fine dining',
              'Albanian cuisine · wine & vineyards · coffee culture · markets',
              'Festivals · folk dancers · churches & mosques · historic castles',
              'Yachts & marinas · helicopters · scenic roads · adventure sports',
              'Couples · families · solo travelers · sunrise & sunset scenes',
            ],
          },
          {
            type: 'dodont',
            dos: ['Name files descriptively (ksamil-beach-drone-june.jpg)', 'Upload highest resolution available', 'Tag people-shots for testimonial reuse'],
            donts: ['Store screenshots of images — always originals', 'Mix personal photos into brand buckets'],
          },
          {
            type: 'check',
            question: 'Why maintain a categorized B-roll library?',
            choices: [
              'So any post type can instantly pull cohesive, on-brand visuals',
              'To use more storage',
              'Because AI images cannot be reused',
              'It is required to log in',
            ],
            correctIndex: 0,
            explain: 'A consistent, categorized library lets the engine and the team mix and match visuals while maintaining one cohesive luxury aesthetic.',
          },
        ],
        notes:
          'Read four or five B-roll categories aloud — the specificity itself sells the professionalism. Remind: real photos for testimonials and team, AI for aspirational scenery, both live side-by-side in the same buckets.',
        takeaways: [
          'One shared library, organized by scene category',
          'Descriptive filenames pay for themselves daily',
          'AI and real photography complement each other',
        ],
      },
    ],
  },
];
